/**
 * Shell that routes between the two RM Inward flows:
 *   - Inward with invoice (existing RMInward page, untouched)
 *   - Inward without invoice (new RMInwardNoInvoice tab)
 *
 * The shell also brokers the "Create Invoice" handoff: when the user clicks
 * Create Invoice on a no-invoice entry, we lift that entry into `prefill`,
 * switch tabs, and the RMInward page picks up `prefill` to open its
 * create-bill dialog pre-populated. The user finalises tax/freight/etc and
 * saves — the backend sees `linked_no_invoice_entry_id` in the payload and
 * marks the entry INVOICED.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RMInward from "./RMInward";
import RMInwardNoInvoice from "./RMInwardNoInvoice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RMInwardShell() {
  const [tab, setTab] = useState("with-invoice");
  const [vendors, setVendors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [availableRMs, setAvailableRMs] = useState([]);
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [vRes, bRes, rmRes] = await Promise.all([
          axios.get(`${API}/vendors`),
          axios.get(`${API}/branches`),
          axios.get(`${API}/raw-materials`, { params: { limit: 5000 } }),
        ]);
        setVendors(Array.isArray(vRes.data) ? vRes.data : (vRes.data.items || []));
        setBranches(Array.isArray(bRes.data) ? bRes.data : (bRes.data.items || []));
        setAvailableRMs(Array.isArray(rmRes.data) ? rmRes.data : (rmRes.data.items || []));
      } catch {
        /* lists degrade gracefully */
      }
    })();
  }, []);

  // No-invoice → with-invoice handoff
  const startCreateInvoice = (entry) => {
    setPrefill(entry);
    setTab("with-invoice");
  };

  return (
    <div className="p-6 md:p-8" data-testid="rm-inward-shell">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4" data-testid="rm-inward-tabs">
          <TabsTrigger value="with-invoice" data-testid="rm-inward-tab-with-invoice">
            Inward with invoice
          </TabsTrigger>
          <TabsTrigger value="without-invoice" data-testid="rm-inward-tab-without-invoice">
            Inward without invoice
          </TabsTrigger>
        </TabsList>

        <TabsContent value="with-invoice" className="mt-0">
          <div className="-m-6 md:-m-8">
            <RMInward
              prefill={prefill}
              onPrefillConsumed={() => setPrefill(null)}
            />
          </div>
        </TabsContent>

        <TabsContent value="without-invoice" className="mt-0">
          <RMInwardNoInvoice
            vendors={vendors}
            branches={branches}
            availableRMs={availableRMs}
            onSwitchTab={setTab}
            onStartCreateInvoice={startCreateInvoice}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
